import { ApiEnums, ApiTypes } from '@app/api';
import { UnitNameParams } from '@core/providers/context/context.service';

export enum AskFrequencyKind {
  EVERY_TIME = 'EVERY_TIME',
  ONLY_ONCE = 'ONLY_ONCE',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
}

/** The stored form of an ask frequency, shared by campaigns and OMQ. */
export interface AskFrequency {
  whenToAsk: string | null;
  countingScope: ApiEnums.CountingScope;
}

const COUNTING_SCOPES: Record<ApiTypes.CountingScope, ApiEnums.CountingScope> = {
  venue: ApiEnums.CountingScope.Venue,
  account: ApiEnums.CountingScope.Account,
};

export function toCountingScope(scope: ApiTypes.CountingScope): ApiEnums.CountingScope {
  return COUNTING_SCOPES[scope];
}

export interface ParsedWhenToAsk {
  kind: AskFrequencyKind;
  count: number | null;
}

export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

export const ASK_FREQUENCY_MAX_WEEKS = 52;
export const ASK_FREQUENCY_MAX_MONTHS = 12;

export const ASK_FREQUENCY_KINDS: readonly AskFrequencyKind[] = [
  AskFrequencyKind.EVERY_TIME,
  AskFrequencyKind.WEEKS,
  AskFrequencyKind.MONTHS,
  AskFrequencyKind.ONLY_ONCE,
];

export const ASK_FREQUENCY_KIND_LABEL_KEYS: Record<AskFrequencyKind, string> = {
  [AskFrequencyKind.EVERY_TIME]: 'SHARED.SYSTEM.EVERY_TIME',
  [AskFrequencyKind.WEEKS]: 'SHARED.SYSTEM.ONLY_X_TIME_WEEKS',
  [AskFrequencyKind.MONTHS]: 'SHARED.SYSTEM.ONLY_X_TIME_MONTHS',
  [AskFrequencyKind.ONLY_ONCE]: 'SHARED.SYSTEM.ONLY_ONCE',
};

/** `null` every time, `"1"` only once, `"<n>W"` every n weeks, `"<n>M"` every n months. */
export function parseWhenToAsk(whenToAsk: string | null | undefined): ParsedWhenToAsk {
  if (!whenToAsk) {
    return { kind: AskFrequencyKind.EVERY_TIME, count: null };
  }
  if (whenToAsk === '1') {
    return { kind: AskFrequencyKind.ONLY_ONCE, count: null };
  }
  const count = Number(whenToAsk.slice(0, -1));
  return whenToAsk.endsWith('W')
    ? { kind: AskFrequencyKind.WEEKS, count }
    : { kind: AskFrequencyKind.MONTHS, count };
}

/** A repeating frequency with no count yet stores as every time. */
export function toWhenToAsk(kind: AskFrequencyKind, count: number | null): string | null {
  if (isRepeatingKind(kind) && count === null) {
    return null;
  }
  switch (kind) {
    case AskFrequencyKind.ONLY_ONCE:
      return '1';
    case AskFrequencyKind.WEEKS:
      return `${count}W`;
    case AskFrequencyKind.MONTHS:
      return `${count}M`;
    default:
      return null;
  }
}

export function isRepeatingKind(kind: AskFrequencyKind | null | undefined): boolean {
  return kind === AskFrequencyKind.WEEKS || kind === AskFrequencyKind.MONTHS;
}

/** Whether the count is one a repeating frequency can store; any count passes otherwise. */
export function isValidAskFrequencyCount(
  kind: AskFrequencyKind | null | undefined,
  count: number | null
): boolean {
  const max = askFrequencyMaxCount(kind);
  return max === null || (count !== null && Number.isInteger(count) && count >= 1 && count <= max);
}

export function askFrequencyMaxCount(kind: AskFrequencyKind | null | undefined): number | null {
  if (kind === AskFrequencyKind.WEEKS) {
    return ASK_FREQUENCY_MAX_WEEKS;
  }
  if (kind === AskFrequencyKind.MONTHS) {
    return ASK_FREQUENCY_MAX_MONTHS;
  }
  return null;
}

/**
 * Words a frequency the same way everywhere it is shown, e.g. "Every 4 weeks, per venue".
 * Every time has no counting, so it never carries the counting part; nor does a frequency
 * shown without a counting scope.
 */
export function askFrequencyLabel(
  whenToAsk: string | null | undefined,
  countingScope: ApiEnums.CountingScope | null | undefined,
  unitNames: UnitNameParams,
  translate: TranslateFn
): string {
  const { kind, count } = parseWhenToAsk(whenToAsk);
  const frequency = frequencyText(kind, count, translate);

  if (kind === AskFrequencyKind.EVERY_TIME || !countingScope) {
    return frequency;
  }

  return translate(
    countingScope === ApiEnums.CountingScope.Account
      ? 'SHARED.ASK_FREQUENCY.LABEL_PER_ACCOUNT'
      : 'SHARED.ASK_FREQUENCY.LABEL_PER_UNIT',
    { ...unitNames, frequency }
  );
}

function frequencyText(
  kind: AskFrequencyKind,
  count: number | null,
  translate: TranslateFn
): string {
  switch (kind) {
    case AskFrequencyKind.ONLY_ONCE:
      return translate('SHARED.SYSTEM.ONLY_ONCE');
    case AskFrequencyKind.WEEKS:
      return count === 1
        ? translate('SHARED.ASK_FREQUENCY.EVERY_WEEK')
        : translate('SHARED.SYSTEM.EVERY_X_TIME_WEEKS', { 0: count });
    case AskFrequencyKind.MONTHS:
      return count === 1
        ? translate('SHARED.ASK_FREQUENCY.EVERY_MONTH')
        : translate('SHARED.SYSTEM.EVERY_X_TIME_MONTHS', { 0: count });
    default:
      return translate('SHARED.SYSTEM.EVERY_TIME');
  }
}
